import { formatEssayShortDate } from 'pages/ApplicationDetailsPage/components/Essay/utils/formatEssayDate'
import {
	mapEssayVersionStatusLabel,
	normalizeEssayVersionStatus,
} from 'pages/ApplicationDetailsPage/components/Essay/utils/essayVersionStatus'

export function essaySummarySnapshot(essay) {
	if (!essay) return null
	return {
		updatedAt: essay.updatedAt || '',
		status: essay.status || '',
		displayVersionId: String(essay.displayVersionId || ''),
		latestVersionUpdatedAt: essay.latestVersionUpdatedAt || '',
		unreadFeedbackCount: Number(essay.unreadFeedbackCount) || 0,
		versionCount: Number(essay.versionCount) || 0,
		title: essay.title || '',
		isNew: Boolean(essay.isNew),
	}
}

export function snapshotsEqual(a, b) {
	if (!a && !b) return true
	if (!a || !b) return false
	return (
		a.updatedAt === b.updatedAt &&
		a.status === b.status &&
		a.displayVersionId === b.displayVersionId &&
		a.latestVersionUpdatedAt === b.latestVersionUpdatedAt &&
		a.unreadFeedbackCount === b.unreadFeedbackCount &&
		a.versionCount === b.versionCount &&
		a.title === b.title &&
		a.isNew === b.isNew
	)
}

export function summaryHasDrawerFields(essay) {
	const snap = essaySummarySnapshot(essay)
	if (!snap) return false
	return Boolean(
		snap.displayVersionId &&
			snap.versionCount > 0 &&
			snap.updatedAt &&
			snap.status,
	)
}

export function normalizeVersionMetadataRow(row) {
	if (!row) return null
	return {
		id: String(row.id || ''),
		versionNumber: Number(row.version_number) || 1,
		status: row.status || 'draft',
		title: row.title || '',
		updatedAt: row.updated_at || row.created_at || '',
		unreadCounselorCount: Number(row.unread_counselor_count) || 0,
		isDraft: String(row.status || '').toLowerCase() === 'draft',
	}
}

export function normalizeVersionMetadataList(rows) {
	return (Array.isArray(rows) ? rows : [])
		.map(normalizeVersionMetadataRow)
		.filter(Boolean)
		.sort((a, b) => b.versionNumber - a.versionNumber)
}

export function splitVersionsByDraft(versions) {
	const list = Array.isArray(versions) ? versions : []
	return {
		priority: list.filter(v => !v.isDraft),
		drafts: list.filter(v => v.isDraft),
	}
}

export function getEssayTypeLabel(essay) {
	const type = String(essay?.type || essay?.scope || '')
		.trim()
		.toLowerCase()
	if (type === 'common_app') return 'Common App Essay'
	if (type === 'university') return 'University Essay'
	return 'Essay'
}

export function essayStatusKeyToSlideTone(statusKey) {
	const key = normalizeEssayVersionStatus(statusKey)
	const map = {
		submitted_for_review: 'review',
		draft_only: 'draft',
		approved: 'approved',
		revision_requested: 'urgent',
		updating: 'updating',
	}
	return map[key] || 'draft'
}

export function buildEssayListCardProps(essay, metadataByEssayId = {}) {
	const meta = metadataByEssayId?.[essay?.id]
	const statusKey = normalizeEssayVersionStatus(essay?.status)
	return {
		title: essay?.title || 'Essay',
		statusKey,
		statusLabel: mapEssayVersionStatusLabel(essay?.status),
		dateLabel: formatEssayShortDate(
			essay?.latestVersionUpdatedAt || essay?.updatedAt,
		),
		hasNew: Boolean(essay?.isNew),
		unreadFeedbackCount:
			Number(essay?.unreadFeedbackCount) ||
			Number(meta?.unreadTotal) ||
			0,
		versionsCount: Number(essay?.versionCount) || Number(meta?.versionCount) || 0,
	}
}

export function buildEssaySlideCardProps(essay) {
	const statusSource = essay?.status || essay?.apiStatus || ''
	return {
		typeLabel: getEssayTypeLabel(essay),
		title: essay?.title || 'Essay',
		statusLabel: mapEssayVersionStatusLabel(statusSource),
		statusTone: essayStatusKeyToSlideTone(statusSource),
		dateLabel: formatEssayShortDate(
			essay?.latestVersionUpdatedAt || essay?.updatedAt,
		),
		hasNew: Boolean(essay?.isNew),
		unreadFeedbackCount: Number(essay?.unreadFeedbackCount) || 0,
	}
}

export function buildEssayListItems(essays, metadataByEssayId = {}) {
	return (Array.isArray(essays) ? essays : []).map(essay => {
		const card = buildEssayListCardProps(essay, metadataByEssayId)
		return {
			id: essay.id,
			...card,
			unreadStudentQuestions: card.unreadFeedbackCount,
		}
	})
}

export function resolveEssayThreadTitle(essay, fallback = 'Essay') {
	return String(essay?.title || '')
		.trim() || fallback
}

export function buildApplicationDrawerItem({ essay, row, versionMeta, versionRow }) {
	const versionId =
		String(versionRow?.id || '') ||
		String(versionMeta?.id || '') ||
		String(essay?.displayVersionId || '')
	const versionNumber =
		Number(versionRow?.version_number) ||
		Number(versionMeta?.versionNumber) ||
		1

	return {
		essayId: String(essay?.id || ''),
		versionId,
		versionNumber,
		title: resolveEssayThreadTitle(essay),
		studentName: row?.studentName || row?.student?.name || 'Student',
		universityName: row?.universityName || row?.university || '',
		countryCode: row?.country || '',
		programName: row?.programName || row?.program || '',
		wordLimit: Number(row?.wordLimit) > 0 ? Number(row.wordLimit) : 600,
		isNew: Boolean(essay?.isNew),
		updatedAt: essay?.updatedAt || versionRow?.updated_at || '',
		createdAt: versionRow?.created_at || essay?.updatedAt || '',
	}
}

export function contentSnapshotFromVersion(version, essayTitle) {
	if (!version) return null
	let contentJson = version.content_json
	if (typeof contentJson === 'string') {
		try {
			contentJson = JSON.parse(contentJson)
		} catch {
			contentJson = null
		}
	}
	const title =
		typeof essayTitle === 'string'
			? String(essayTitle).trim() || 'Essay'
			: resolveEssayThreadTitle(essayTitle)

	return {
		title,
		status: version.status || '',
		updatedAt: version.updated_at || '',
		contentText: version.content_text || '',
		contentJson,
		anchorHash: hashAnchorsFromVersion(version),
	}
}

function hashAnchorsFromVersion(version) {
	let contentJson = version?.content_json
	if (typeof contentJson === 'string') {
		try {
			contentJson = JSON.parse(contentJson)
		} catch {
			contentJson = null
		}
	}
	const doc = contentJson?.content || contentJson
	if (!doc) return ''
	try {
		return JSON.stringify(doc).length.toString()
	} catch {
		return ''
	}
}

export function snapshotsContentEqual(a, b) {
	if (!a && !b) return true
	if (!a || !b) return false
	return (
		a.title === b.title &&
		a.status === b.status &&
		a.updatedAt === b.updatedAt &&
		a.contentText === b.contentText &&
		a.anchorHash === b.anchorHash
	)
}

export function metadataUnreadTotal(versions) {
	return (Array.isArray(versions) ? versions : []).reduce(
		(sum, v) => sum + (Number(v?.unreadCounselorCount) || 0),
		0,
	)
}
